// FILE: src/services/readinessService.js
//
// Phase C — Readiness Tracker. Implements the 6-input weighted-sum formula
// from PLAN-phase-a-design.md §4 and persists snapshots via the
// record_readiness_snapshot SECURITY DEFINER RPC (migration 020).
//
//   composite =
//       0.40 * rolling_correctness     (exponentially recency-weighted avg of last 5)
//     + 0.20 * phase_coverage          (% of phases with >= 10 attempts AND >= 70% acc)
//     + 0.15 * full_sim_count          (min(count, 3) / 3 * 100)
//     + 0.10 * weak_area_improvement   (Δ on weakest 2 phases, clamped ±25)
//     + 0.10 * recency                 (max(0, 100 - 7 * days_since))
//     + 0.05 * time_management         (% sessions NOT expired with ≥ 50%)
//
// Status buckets:
//   0 attempts → not_started
//   1–39       → building_foundation
//   40–74      → getting_close
//   75–100     → exam_ready
//
// Source of truth: question_attempts joined to questions.category, surfaced
// via the learner_session_summary view (§6.9.2). Sessions older than the
// active baseline_at_<cert> are excluded; full history remains in
// Performance History (§6.10.1).
//
// All functions are pure or fire-and-forget. Errors are logged, never thrown.

import { supabase } from './supabaseClient';
import { fireAchievement, readinessAchievementsToFire } from './achievementService';
import { getExamConfig } from '../config/exams';

// ── Public: orchestration ───────────────────────────────────────────────────

/**
 * Fetch the user's sessions for a cert (post-baseline if a baseline exists),
 * compute readiness, and record a snapshot via the SECURITY DEFINER RPC.
 *
 * @param {object} params
 * @param {string} params.examId      — 'pmp' | 'cpmai'
 * @param {string} params.baselineAt  — ISO date or null
 * @returns {Promise<{ snapshotId: string|null, readiness: object } | null>}
 */
export async function recordReadinessSnapshot({ examId, baselineAt }) {
  try {
    const sessions = await loadSessionsForReadiness(examId, baselineAt);
    const readiness = computeReadiness({ examId, sessions, baselineAt });

    // Don't persist a snapshot for not_started — nothing to track yet.
    if (readiness.status === 'not_started') {
      return { snapshotId: null, readiness };
    }

    // Look up the previous status BEFORE persisting the new snapshot so
    // we can fire exam_ready_reached on the transition (Phase E1).
    let previousStatus = null;
    try {
      const prior = await loadReadinessSnapshots(examId, 1);
      previousStatus = prior[0]?.status || null;
    } catch (_) { /* non-fatal */ }

    const { data, error } = await supabase.rpc('record_readiness_snapshot', {
      p_exam_id:     examId,
      p_score:       readiness.score,
      p_status:      readiness.status,
      p_inputs:      readiness.inputs,
      p_weak_phases: readiness.weakPhases.map((w) => w.phase),
    });

    if (error) {
      console.warn('[readinessService] snapshot RPC failed:', error.message);
      return { snapshotId: null, readiness };
    }

    // Phase E1 — fire readiness-driven achievements based on transition.
    try {
      const fires = readinessAchievementsToFire({
        status: readiness.status, previousStatus, examId,
      });
      for (const a of fires) {
        await fireAchievement(a);
      }
    } catch (_) { /* achievements are best-effort */ }

    return { snapshotId: data || null, readiness };
  } catch (err) {
    console.warn('[readinessService] unexpected error:', err?.message || err);
    return null;
  }
}

/**
 * Load the sessions used by the readiness formula. Filters to the active cert
 * and (when a baseline exists) to sessions on or after the baseline date.
 *
 * @param {string} examId
 * @param {string|null} baselineAt — ISO date
 * @returns {Promise<object[]>} normalized rows
 */
export async function loadSessionsForReadiness(examId, baselineAt = null) {
  try {
    let query = supabase
      .from('learner_session_summary')
      .select('*')
      .eq('exam_id', examId)
      .order('started_at', { ascending: false });

    if (baselineAt) query = query.gte('started_at', baselineAt);

    const { data, error } = await query;
    if (error) {
      console.warn('[readinessService] load sessions failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data.map(normalizeSessionRow) : [];
  } catch (err) {
    console.warn('[readinessService] unexpected error reading sessions:', err?.message || err);
    return [];
  }
}

/**
 * Load the most recent readiness snapshots for the learner. Returns rows
 * ordered newest first. Phase D will use this to draw a readiness trend line.
 *
 * @param {string} examId
 * @param {number} [limit=20]
 */
export async function loadReadinessSnapshots(examId, limit = 20) {
  try {
    const { data, error } = await supabase
      .from('readiness_snapshots')
      .select('*')
      .eq('exam_id', examId)
      .order('computed_at', { ascending: false })
      .limit(limit);
    if (error) {
      console.warn('[readinessService] load snapshots failed:', error.message);
      return [];
    }
    return Array.isArray(data) ? data : [];
  } catch (err) {
    console.warn('[readinessService] unexpected error reading snapshots:', err?.message || err);
    return [];
  }
}

// ── Pure: computeReadiness ──────────────────────────────────────────────────

/**
 * Computes a readiness score (0–100), a status bucket, and the six input
 * factors that produced the score. Pure — easy to unit-test.
 *
 * @param {object} params
 * @param {string} params.examId
 * @param {object[]} params.sessions   — newest-first normalized rows
 * @param {string|null} [params.baselineAt]
 * @param {Date} [params.now]
 * @returns {ReadinessResult}
 *
 * ReadinessResult:
 *   {
 *     score: number,                // 0 – 100
 *     status: 'not_started' | 'building_foundation' | 'getting_close' | 'exam_ready',
 *     inputs: {
 *       rolling_correctness: { value, weight, label, evidence },
 *       phase_coverage:      { ... },
 *       full_sim_count:      { ... },
 *       weak_area_improvement: { ... },
 *       recency:             { ... },
 *       time_management:     { ... },
 *     },
 *     weakPhases: Array<{ phase, accuracy, total, correct }>,
 *   }
 */
export function computeReadiness({ examId, sessions, baselineAt = null, now = new Date() }) {
  // Cert config drives the readiness thresholds — no hardcoded cert assumptions.
  // Defaults (75 / 3) reproduce historical behavior for any cert that does not
  // override them, so PMP / CPMAI / PMI-ACP results are unchanged.
  const _cfg = (examId && getExamConfig(examId)) || null;
  const passTarget = (_cfg && typeof _cfg.pass_target_pct === 'number') ? _cfg.pass_target_pct : 75;
  const simTarget  = (_cfg && typeof _cfg.readiness_full_sim_target === 'number') ? _cfg.readiness_full_sim_target : 3;
  const safe = Array.isArray(sessions) ? sessions : [];
  if (safe.length === 0) {
    return {
      score: 0,
      status: 'not_started',
      inputs: emptyInputs(),
      weakPhases: [],
    };
  }

  const rolling = computeRollingCorrectness(safe, now);
  const coverage = computePhaseCoverage(safe);
  const sims = computeFullSimCount(safe, simTarget);
  const improvement = computeWeakAreaImprovement(safe);
  const recency = computeRecency(safe, now);
  const timeMgmt = computeTimeManagement(safe);

  const score =
      0.40 * rolling.value
    + 0.20 * coverage.value
    + 0.15 * sims.value
    + 0.10 * improvement.value
    + 0.10 * recency.value
    + 0.05 * timeMgmt.value;

  const clamped = Math.max(0, Math.min(100, score));
  const rounded = Math.round(clamped * 10) / 10;

  return {
    score: rounded,
    status: statusFromScore(rounded, safe.length, passTarget),
    inputs: {
      rolling_correctness:   { ...rolling,     weight: 0.40 },
      phase_coverage:        { ...coverage,    weight: 0.20 },
      full_sim_count:        { ...sims,        weight: 0.15 },
      weak_area_improvement: { ...improvement, weight: 0.10 },
      recency:               { ...recency,     weight: 0.10 },
      time_management:       { ...timeMgmt,    weight: 0.05 },
    },
    weakPhases: coverage.weakPhases || [],
  };
}

// ── Bucketing ───────────────────────────────────────────────────────────────

export function statusFromScore(score, sessionCount, passTarget = 75) {
  if (!sessionCount || sessionCount === 0) return 'not_started';
  if (score >= passTarget) return 'exam_ready';
  if (score >= 40) return 'getting_close';
  return 'building_foundation';
}

export const STATUS_LABEL = {
  not_started:        'Not started',
  building_foundation: 'Building foundation',
  getting_close:      'Getting close',
  exam_ready:         'Exam ready',
};

// ── Individual inputs ───────────────────────────────────────────────────────

/**
 * Recency-weighted average of last 5 sessions' scorePercentage.
 * Exponential half-life of 7 days, anchored at `now`.
 */
export function computeRollingCorrectness(sessions, now) {
  const last5 = sessions.slice(0, 5).filter((s) => typeof s.scorePercentage === 'number');
  if (last5.length === 0) {
    return { value: 0, label: 'No completed sessions yet.', evidence: { count: 0 } };
  }

  const halfLifeDays = 7;
  let weightedSum = 0;
  let weightSum   = 0;
  for (const s of last5) {
    const days = ageDays(s.startedAt, now);
    const w = Math.pow(0.5, days / halfLifeDays);
    weightedSum += s.scorePercentage * w;
    weightSum   += w;
  }
  const avg = weightSum > 0 ? weightedSum / weightSum : 0;
  const value = Math.max(0, Math.min(100, avg));

  return {
    value: round1(value),
    label: `Your last ${last5.length} session${last5.length === 1 ? '' : 's'} averaged ${round1(value)}%${value < 80 ? ' — aim for 80%+ to be exam-ready.' : '.'}`,
    evidence: { count: last5.length, raw_avg: round1(avg) },
  };
}

/**
 * % of distinct phases that have BOTH >= 10 total attempts AND >= 70%
 * accuracy. Pulls phase_breakdown from the view rows.
 */
export function computePhaseCoverage(sessions) {
  // Aggregate by phase across ALL provided sessions (already post-baseline).
  const agg = {}; // phase -> { total, correct }
  for (const s of sessions) {
    const pb = s.phaseBreakdown || {};
    for (const phase of Object.keys(pb)) {
      const e = pb[phase] || {};
      const total   = Number(e.total)   || 0;
      const correct = Number(e.correct) || 0;
      if (!agg[phase]) agg[phase] = { total: 0, correct: 0 };
      agg[phase].total   += total;
      agg[phase].correct += correct;
    }
  }
  const phases = Object.keys(agg);
  if (phases.length === 0) {
    return { value: 0, label: 'No phase data yet.', evidence: { phases: 0 }, weakPhases: [] };
  }

  let qualifying = 0;
  const weak = [];
  for (const p of phases) {
    const { total, correct } = agg[p];
    const acc = total > 0 ? (100 * correct) / total : 0;
    if (total >= 10 && acc >= 70) qualifying += 1;
    if (total >= 5 && acc < 70) {
      weak.push({ phase: p, accuracy: Math.round(acc), total, correct });
    }
  }
  const value = (100 * qualifying) / phases.length;
  weak.sort((a, b) => a.accuracy - b.accuracy);

  return {
    value: round1(value),
    label: `${qualifying} of ${phases.length} phase${phases.length === 1 ? '' : 's'} meet the coverage threshold (≥10 attempts AND ≥70%).`,
    evidence: { phases: phases.length, qualifying },
    weakPhases: weak.slice(0, 3),
  };
}

/** min(count, target) / target * 100 — capped reward at `target` sims. */
export function computeFullSimCount(sessions, target = 3) {
  const safeTarget = target > 0 ? target : 3;
  const count = sessions.filter((s) => s.mode === 'full').length;
  const capped = Math.min(count, safeTarget);
  const value = (capped / safeTarget) * 100;
  return {
    value: round1(value),
    label: `You've completed ${count} of ${safeTarget} recommended full-length simulation${safeTarget === 1 ? '' : 's'}.`,
    evidence: { count, capped, target: safeTarget },
  };
}

/**
 * Δ in accuracy on the 2 weakest phases between the first and second halves
 * of the last 10 attempts. Clamped to ±25, mapped to 0–100 (50 = no change).
 */
export function computeWeakAreaImprovement(sessions) {
  const last10 = sessions.slice(0, 10);
  if (last10.length < 4) {
    return { value: 50, label: 'Not enough recent sessions to detect movement.', evidence: { sessions: last10.length } };
  }
  const half = Math.floor(last10.length / 2);
  const recent = last10.slice(0, half);
  const earlier = last10.slice(half);

  // For each phase, compute accuracy in recent and earlier halves; identify
  // bottom 2 by earlier-half accuracy; delta = recent - earlier on those.
  const earlierAgg = aggregatePhases(earlier);
  const recentAgg  = aggregatePhases(recent);

  const phaseAccuracies = Object.keys(earlierAgg).map((p) => {
    const e = earlierAgg[p];
    const acc = e.total > 0 ? (100 * e.correct) / e.total : 0;
    return { phase: p, accuracy: acc, total: e.total };
  }).filter((r) => r.total >= 3).sort((a, b) => a.accuracy - b.accuracy);

  if (phaseAccuracies.length === 0) {
    return { value: 50, label: 'No comparable phase data across the window.', evidence: {} };
  }

  const bottom2 = phaseAccuracies.slice(0, 2);
  let deltaSum = 0;
  let n = 0;
  for (const { phase } of bottom2) {
    const e = earlierAgg[phase];
    const r = recentAgg[phase];
    if (!e || !r || e.total === 0 || r.total === 0) continue;
    const eAcc = (100 * e.correct) / e.total;
    const rAcc = (100 * r.correct) / r.total;
    deltaSum += (rAcc - eAcc);
    n += 1;
  }

  if (n === 0) {
    return { value: 50, label: 'Weakest phases haven\'t been revisited recently.', evidence: {} };
  }

  const avgDelta = deltaSum / n;
  const clamped = Math.max(-25, Math.min(25, avgDelta));
  const value = 50 + (clamped / 25) * 50; // map -25..+25 → 0..100

  return {
    value: round1(value),
    label:
      avgDelta > 2  ? `Your weakest phases improved by ${round1(avgDelta)} points recently.` :
      avgDelta < -2 ? `Your weakest phases dipped ${round1(-avgDelta)} points recently — worth a focused session.` :
                       'Your weakest phases held steady.',
    evidence: { avgDelta: round1(avgDelta) },
  };
}

/** Decay from 100 to 0 over 14 days. */
export function computeRecency(sessions, now) {
  if (sessions.length === 0) return { value: 0, label: 'No sessions yet.', evidence: { days_since: null } };
  const latest = sessions[0];
  const days = ageDays(latest.startedAt, now);
  const value = Math.max(0, 100 - 7 * days);
  return {
    value: round1(value),
    label:
      days <= 1  ? 'You studied within the last day.' :
      days <= 3  ? `Last session was ${Math.round(days)} days ago — momentum is fresh.` :
      days <= 7  ? `Last session was ${Math.round(days)} days ago — a short session this week keeps things sharp.` :
                   `Last session was ${Math.round(days)} days ago — let's pick up with something light.`,
    evidence: { days_since: round1(days) },
  };
}

/**
 * % of completed sessions that finished WITHOUT 'expired' completion_reason
 * AND scored ≥ 50. A rough "could you both pace yourself and answer" signal.
 */
export function computeTimeManagement(sessions) {
  const finished = sessions.filter((s) => typeof s.scorePercentage === 'number');
  if (finished.length === 0) {
    return { value: 0, label: 'No completed sessions yet.', evidence: { sessions: 0 } };
  }
  const ok = finished.filter(
    (s) => s.completionReason !== 'expired' && s.scorePercentage >= 50,
  ).length;
  const value = (100 * ok) / finished.length;
  return {
    value: round1(value),
    label:
      value >= 80 ? 'Time management is on track.' :
      value >= 50 ? 'Time management is mostly on track — keep an eye on full simulations.' :
                    'Pacing is dragging some sessions — try a timed practice without pausing.',
    evidence: { ok, finished: finished.length },
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function emptyInputs() {
  const stub = (label) => ({ value: 0, label, evidence: {}, weight: 0 });
  return {
    rolling_correctness:   stub('No sessions yet.'),
    phase_coverage:        stub('No sessions yet.'),
    full_sim_count:        stub('No sessions yet.'),
    weak_area_improvement: stub('No sessions yet.'),
    recency:               stub('No sessions yet.'),
    time_management:       stub('No sessions yet.'),
  };
}

function aggregatePhases(sessionsSubset) {
  const out = {};
  for (const s of sessionsSubset) {
    const pb = s.phaseBreakdown || {};
    for (const p of Object.keys(pb)) {
      const e = pb[p] || {};
      if (!out[p]) out[p] = { total: 0, correct: 0 };
      out[p].total   += Number(e.total)   || 0;
      out[p].correct += Number(e.correct) || 0;
    }
  }
  return out;
}

function ageDays(iso, now) {
  if (!iso) return 9999;
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return 9999;
  return Math.max(0, (now.getTime() - then) / 86400000);
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

/**
 * Map a learner_session_summary view row into the shape this service uses.
 * Mirrors performanceHistoryService.normalizeRow but kept local to avoid a
 * cross-service import; both are tiny.
 */
export function normalizeSessionRow(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    sessionId:        row.session_id,
    userId:           row.user_id,
    examId:           row.exam_id,
    mode:             row.mode,
    startedAt:        row.started_at,
    endedAt:          row.ended_at,
    completionReason: row.completion_reason,
    scorePercentage:  typeof row.score_percentage === 'number' ? row.score_percentage : null,
    scoreCorrect:     typeof row.score_correct    === 'number' ? row.score_correct    : 0,
    scoreIncorrect:   typeof row.score_incorrect  === 'number' ? row.score_incorrect  : 0,
    scoreSkipped:     typeof row.score_skipped    === 'number' ? row.score_skipped    : 0,
    scoreUnanswered:  typeof row.score_unanswered === 'number' ? row.score_unanswered : 0,
    totalQuestions:   typeof row.total_questions  === 'number' ? row.total_questions  : 0,
    durationSeconds:  typeof row.duration_seconds === 'number' ? row.duration_seconds : null,
    source:           row.source,
    phaseBreakdown:   row.phase_breakdown || null,
  };
}

// ── Confidence Forecast (Phase D) ───────────────────────────────────────────
//
// Linear projection of readiness over time, from the snapshot history,
// toward the learner's target score. Requires at least 3 snapshots to
// avoid false precision; below that, returns { sufficient: false } with
// coaching copy.
//
// Output:
//   {
//     sufficient: true,
//     slope: number,                    // points-per-day (positive = improving)
//     intercept: number,                // score at the most-recent snapshot
//     projectedDate: string|null,       // ISO date when score crosses target
//                                       // null if slope ≤ 0 or already there
//     targetScore: number,
//     basedOn: number,                  // how many snapshots fed the fit
//     latestScore: number,
//     latestAt: string,                 // ISO date
//   }
// or
//   { sufficient: false, message: '...' }

/**
 * @param {object[]} snapshots — newest-first rows from readiness_snapshots
 * @param {number} targetScore — 0–100
 * @returns {object}
 */
export function computeConfidenceForecast(snapshots, targetScore) {
  if (!Array.isArray(snapshots) || snapshots.length < 3) {
    return {
      sufficient: false,
      message: 'A few more sessions and you\'ll see your confidence forecast.',
      basedOn: Array.isArray(snapshots) ? snapshots.length : 0,
    };
  }
  if (typeof targetScore !== 'number' || targetScore <= 0) {
    targetScore = 75;
  }

  // Use the most recent 8 snapshots for the fit — keeps the projection
  // responsive to recent improvement rather than ancient data.
  const recent = snapshots.slice(0, Math.min(8, snapshots.length))
    .map((s) => ({ t: new Date(s.computed_at).getTime() / 86400000, y: Number(s.score) }))
    .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.y))
    .sort((a, b) => a.t - b.t); // oldest first for stable regression

  if (recent.length < 3) {
    return {
      sufficient: false,
      message: 'A few more sessions and you\'ll see your confidence forecast.',
      basedOn: recent.length,
    };
  }

  // Ordinary least squares: y = m·t + b.
  const n = recent.length;
  const sumT  = recent.reduce((a, p) => a + p.t, 0);
  const sumY  = recent.reduce((a, p) => a + p.y, 0);
  const sumTT = recent.reduce((a, p) => a + p.t * p.t, 0);
  const sumTY = recent.reduce((a, p) => a + p.t * p.y, 0);
  const denom = n * sumTT - sumT * sumT;
  const slope     = denom === 0 ? 0 : (n * sumTY - sumT * sumY) / denom;
  const intercept = denom === 0 ? sumY / n : (sumY - slope * sumT) / n;

  const latest = recent[recent.length - 1];

  let projectedDate = null;
  if (slope > 0 && latest.y < targetScore) {
    const tTarget = (targetScore - intercept) / slope;
    const d = new Date(tTarget * 86400000);
    if (Number.isFinite(d.getTime())) {
      // Clamp to a sane horizon (1 year forward at most).
      const maxHorizon = new Date(latest.t * 86400000 + 365 * 86400000);
      projectedDate = d.getTime() > maxHorizon.getTime() ? null : toIsoDate(d);
    }
  } else if (latest.y >= targetScore) {
    projectedDate = toIsoDate(new Date(latest.t * 86400000));
  }

  return {
    sufficient: true,
    slope:        round2(slope),
    intercept:    round2(intercept),
    projectedDate,
    targetScore,
    basedOn:      n,
    latestScore:  round2(latest.y),
    latestAt:     toIsoDate(new Date(latest.t * 86400000)),
  };
}

function round2(n) { return Math.round(n * 100) / 100; }
function toIsoDate(d) {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── Baseline helpers ────────────────────────────────────────────────────────

/**
 * Calls the set_learner_baseline RPC. Used by both first-baseline auto-set
 * and explicit Recalibrate.
 *
 * @param {string} examId — 'pmp' | 'cpmai'
 * @returns {Promise<string|null>} date string or null on failure
 */
export async function setLearnerBaseline(examId) {
  try {
    const { data, error } = await supabase.rpc('set_learner_baseline', { p_exam_id: examId });
    if (error) {
      console.warn('[readinessService] set_learner_baseline failed:', error.message);
      return null;
    }
    return data || null;
  } catch (err) {
    console.warn('[readinessService] unexpected error setting baseline:', err?.message || err);
    return null;
  }
}
